import { createContext, useContext, useId, type AriaAttributes, type ReactNode } from 'react';
import { cx } from '../../utils/cx';
import s from './FormField.module.css';

type FieldContextValue = {
  id: string;
  describedBy: string | undefined;
  invalid: boolean;
  required: boolean;
};

const FieldContext = createContext<FieldContextValue | null>(null);

/**
 * Props a control spreads onto its input: `id`, `aria-describedby`, `aria-invalid`, `required`.
 * Explicit props passed to the control win over the field's.
 */
export function useFieldProps<
  P extends {
    id?: string;
    'aria-describedby'?: string;
    'aria-invalid'?: AriaAttributes['aria-invalid'];
    required?: boolean;
  },
>(props: P) {
  const field = useContext(FieldContext);
  if (!field) return props;
  return {
    ...props,
    id: props.id ?? field.id,
    'aria-describedby': cx(field.describedBy, props['aria-describedby']) || undefined,
    'aria-invalid': props['aria-invalid'] ?? (field.invalid || undefined),
    required: props.required ?? (field.required || undefined),
  };
}

export type FormFieldProps = {
  label: ReactNode;
  children: ReactNode;
  /** Help text under the label. */
  hint?: ReactNode;
  /** Error message; marks the control invalid. Written kindly (docs/05 voice). */
  error?: ReactNode;
  required?: boolean;
  /** Shows "(optional)" next to the label. */
  optional?: boolean;
  /** Visually hide the label (it stays accessible). */
  hideLabel?: boolean;
  className?: string;
};

/** Label + hint + error for one control, with ids and aria wiring handled for you. */
export function FormField({
  label,
  children,
  hint,
  error,
  required,
  optional,
  hideLabel,
  className,
}: FormFieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const value: FieldContextValue = {
    id,
    describedBy: cx(hintId, errorId) || undefined,
    invalid: !!error,
    required: !!required,
  };

  return (
    <div className={cx(s.field, className)}>
      <label htmlFor={id} className={s.label} data-hidden={hideLabel || undefined}>
        {label}
        {optional ? <span className={s.optional}> (optional)</span> : null}
      </label>
      {hint ? (
        <p id={hintId} className={s.hint}>
          {hint}
        </p>
      ) : null}
      <FieldContext.Provider value={value}>{children}</FieldContext.Provider>
      {error ? (
        <p id={errorId} className={s.error} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
